"use strict";
/** *******************************************************************************************************************
  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
  
  Licensed under the Apache License, Version 2.0 (the "License").
  You may not use this file except in compliance with the License.
  You may obtain a copy of the License at
  
      http://www.apache.org/licenses/LICENSE-2.0
  
  Unless required by applicable law or agreed to in writing, software
  distributed under the License is distributed on an "AS IS" BASIS,
  WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
  See the License for the specific language governing permissions and
  limitations under the License.                                                                              *
 ******************************************************************************************************************** */
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    Object.defineProperty(o, k2, { enumerable: true, get: function() { return m[k]; } });
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importStar(require("react"));
const clsx_1 = __importDefault(require("clsx"));
const react_table_1 = require("react-table");
const core_1 = require("@material-ui/core");
const Container_1 = __importDefault(require("../../layouts/Container"));
const Checkbox_1 = __importDefault(require("../Checkbox"));
const ContainerHeaderContent_1 = __importDefault(require("./components/ContainerHeaderContent"));
const TableHead_1 = __importDefault(require("./components/TableHead"));
const TableBody_1 = __importDefault(require("./components/TableBody"));
const TableFooter_1 = __importDefault(require("./components/TableFooter"));
const SettingsBar_1 = __importDefault(require("./components/SettingsBar"));
const ColumnsSelector_1 = __importDefault(require("./components/ColumnsSelector"));
const ColumnsGrouping_1 = __importDefault(require("./components/ColumnsGrouping"));
const RadioGroup_1 = require("../RadioGroup");
const use_debounce_1 = require("use-debounce");
const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_SIZES = [10, 25, 50];
const DEFAULT_DEBOUNCE_TIMER = 250;
const useStyles = core_1.makeStyles((theme) => ({
    tableBar: {
        display: 'flex',
        alignItems: 'center',
        marginTop: '.5rem',
    },
    tableWrapper: {
        maxHeight: '75vh',
    },
    searchBar: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        marginRight: '10px',
    },
    leftSpace: {
        marginLeft: '10px',
    },
    tableHeadRow: {
        borderTop: 0,
    },
    cellAlign: {
        display: 'inline-flex',
        alignItems: 'flex-end',
        width: 'inherit',
        height: 'max-content',
    },
    loadingTableBlur: {
        filter: 'alpha(opacity=50)',
        opacity: 0.5,
        transition: 'all 0.15s linear',
    },
    loadingSearchBarPadding: {
        paddingRight: '.25rem',
    },
    aggregated: {
        backgroundColor: theme.palette.grey[100],
    },
    verticalGrid: {
        display: 'inline-grid',
    },
    footerCell: {
        textAlign: 'center',
        fontWeight: 700,
        padding: '.5rem',
    },
    ellipsizeText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    resizer: {
        display: 'inline-block',
        borderLeft: `1px solid ${theme.palette.grey[200]}`,
        width: '1px',
        height: '60%',
        position: 'absolute',
        right: 0,
        top: '20%',
        transform: 'translateX(50%)',
        zIndex: 1,
    },
}));
const convertArrayToBooleanObject = (arr) => arr.reduce((map, id) => (Object.assign(Object.assign({}, map), { [id]: true })), {});
/** A table presents data in a two-dimensional format, arranged in columns and rows in a rectangular form. */
function Table(_a) {
    var { actionGroup = null, columnDefinitions = [], defaultGroups = [], defaultPageIndex = 0, defaultPageSize = DEFAULT_PAGE_SIZE, disableGroupBy = true, disablePagination = false, disableSettings = false, disableSortBy = false, disableFilters = false, disableRowSelect = false, items = [], loading = false, onSelectionChange, onFetchData = null, pageSizes = DEFAULT_PAGE_SIZES, tableDescription, tableTitle = '', wrapText = true, selectedRowIds = [], multiSelect = true, getRowId, isItemDisabled, sortBy: defaultSortBy = [], errorText } = _a, props = __rest(_a, ["actionGroup", "columnDefinitions", "defaultGroups", "defaultPageIndex", "defaultPageSize", "disableGroupBy", "disablePagination", "disableSettings", "disableSortBy", "disableFilters", "disableRowSelect", "items", "loading", "onSelectionChange", "onFetchData", "pageSizes", "tableDescription", "tableTitle", "wrapText", "selectedRowIds", "multiSelect", "getRowId", "isItemDisabled", "sortBy", "errorText"]);
    const styles = useStyles({});
    const [groupBy, setGroupBy] = react_1.useState(convertArrayToBooleanObject(defaultGroups));
    const [showColumns, setShowColumns] = react_1.useState(convertArrayToBooleanObject(columnDefinitions.map((column) => column.id || '')));
    const [controlledPageSize, setControlledPageSize] = react_1.useState(defaultPageSize);
    const columns = react_1.useMemo(() => {
        const columnsFiltered = columnDefinitions.filter((column) => showColumns[column.id || '']);
        if (!disableRowSelect) {
            columnsFiltered.unshift({
                id: '_selection_',
                width: 50,
                Header: (props) => {
                    return multiSelect && !isItemDisabled ? (react_1.default.createElement(Checkbox_1.default, Object.assign({ ariaLabel: "Checkbox to select all row items" }, props.getToggleAllRowsSelectedProps()))) : null;
                },
                Cell: (props) => {
                    const { row } = props;
                    const isSelectDisabled = !!(isItemDisabled === null || isItemDisabled === void 0 ? void 0 : isItemDisabled(row.original));
                    return (react_1.default.createElement("div", null, multiSelect ? (react_1.default.createElement(Checkbox_1.default, Object.assign({ ariaLabel: "Checkbox to select row item" }, row.getToggleRowSelectedProps(), { disabled: isSelectDisabled, controlId: row.id }))) : (react_1.default.createElement(RadioGroup_1.RadioButton, { name: "select", checked: row.isSelected, controlId: row.id, disabled: isSelectDisabled, onChange: () => {
                            props.toggleAllRowsSelected(false);
                            row.toggleRowSelected(true);
                        } }))));
                },
            });
        }
        return columnsFiltered;
    }, [columnDefinitions, showColumns, disableRowSelect, multiSelect, isItemDisabled]);
    const rowCount = react_1.useMemo(() => {
        if (typeof props.rowCount === 'undefined') {
            return (items === null || items === void 0 ? void 0 : items.length) || 0;
        }
        return props.rowCount;
    }, [items, props.rowCount]);
    const selectedRowIdMap = react_1.useMemo(() => {
        return convertArrayToBooleanObject(selectedRowIds);
    }, [selectedRowIds]);
    const pageCount = react_1.useMemo(() => {
        return Math.ceil(rowCount / controlledPageSize);
    }, [rowCount, controlledPageSize]);
    const tableOpts = react_1.useMemo(() => (Object.assign(Object.assign({ data: items || [], columns, defaultColumn: {
            minWidth: 50,
            width: 120,
            maxWidth: 800,
        }, initialState: {
            pageIndex: defaultPageIndex,
            pageSize: controlledPageSize,
            selectedRowIds: selectedRowIdMap,
            sortBy: defaultSortBy,
            groupBy: defaultGroups,
        } }, (onFetchData != null && { pageCount })), { getRowId,
        disableSortBy,
        disableGroupBy,
        disableFilters, defaultCanFilter: true, manualFilters: onFetchData != null, manualPagination: onFetchData != null, manualSorting: onFetchData != null, manualSortBy: onFetchData != null, manualGlobalFilter: onFetchData != null, autoResetSortBy: onFetchData === null, autoResetPage: onFetchData === null, autoResetSelectedRows: onFetchData === null, autoResetGlobalFilter: onFetchData === null })), [
        items,
        columns,
        pageCount,
        controlledPageSize,
        defaultGroups,
        defaultPageIndex,
        defaultSortBy,
        disableFilters,
        disableGroupBy,
        disableSortBy,
        getRowId,
        onFetchData,
        selectedRowIdMap,
    ]);
    const { getTableProps, getTableBodyProps, headerGroups, prepareRow, page, rows, gotoPage, nextPage, canNextPage, previousPage, canPreviousPage, setPageSize, selectedFlatRows, setGlobalFilter, toggleGroupBy, state: { pageIndex, pageSize, sortBy, globalFilter }, } = react_table_1.useTable(tableOpts, react_table_1.useBlockLayout, react_table_1.useFilters, react_table_1.useGlobalFilter, react_table_1.useGroupBy, react_table_1.useSortBy, react_table_1.useExpanded, react_table_1.usePagination, react_table_1.useRowSelect, react_table_1.useResizeColumns);
    react_1.useEffect(() => {
        setControlledPageSize(pageSize || DEFAULT_PAGE_SIZE);
    }, [pageSize]);
    const toggleCopy = (target, headerId) => {
        const copy = Object.assign({}, target);
        if (copy[headerId]) {
            delete copy[headerId];
        }
        else {
            copy[headerId] = true;
        }
        return copy;
    };
    const handleShowColumnsChange = (headerId) => {
        if (!headerId) {
            return;
        }
        const showColumnsCopy = toggleCopy(showColumns, headerId);
        setShowColumns(showColumnsCopy);
    };
    const onGroupChange = (headerId) => {
        if (!headerId) {
            return;
        }
        const groupByCopy = toggleCopy(groupBy, headerId);
        setGroupBy(groupByCopy);
        toggleGroupBy(headerId);
    };
    const handleSelectionChangeDebounce = use_debounce_1.useDebouncedCallback((selectedFlatRows) => {
        const selected = selectedFlatRows
            .filter((row) => !row.isGrouped)
            .map((row) => row.original);
        onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(selected);
    }, DEFAULT_DEBOUNCE_TIMER);
    react_1.useEffect(() => {
        if (selectedFlatRows) {
            handleSelectionChangeDebounce(selectedFlatRows);
        }
    }, [selectedFlatRows, handleSelectionChangeDebounce]);
    react_1.useEffect(() => {
        if (onFetchData) {
            const flattenGroupBy = () => Object.keys(groupBy).filter((key) => groupBy[key]);
            const flattenShowColumns = () => Object.keys(showColumns).filter((key) => showColumns[key]);
            onFetchData({
                pageSize: pageSize || 0,
                pageIndex: pageIndex || 0,
                sortBy: sortBy || [],
                groupBy: flattenGroupBy(),
                showColumns: flattenShowColumns(),
                filterText: globalFilter || '',
            });
        }
    }, [onFetchData, pageIndex, pageSize, sortBy, groupBy, showColumns, globalFilter]);
    const columnsSelectorProps = {
        columnDefinitions,
        onShowColumnsChange: handleShowColumnsChange,
        showColumns,
        styles,
    };
    const columnsGroupingProps = {
        columnDefinitions,
        onGroupChange,
        groupBy,
        styles,
    };
    const groupCount = react_1.useMemo(() => {
        return rows.filter((row) => row.isGrouped).length;
    }, [rows]);
    const settingsBarProps = {
        pageIndex: pageIndex || 0,
        pageSize: pageSize || DEFAULT_PAGE_SIZE,
        pageSizes: pageSizes || DEFAULT_PAGE_SIZES,
        pageLength: (page || []).length,
        rowCount: rows.length,
        totalCount: rowCount + groupCount,
        loading,
        disablePagination,
        disableSettings,
        disableGroupBy,
        gotoPage,
        previousPage,
        canPreviousPage,
        nextPage,
        canNextPage,
        setPageSize,
        styles,
        columnsGroupingComponent: react_1.default.createElement(ColumnsGrouping_1.default, Object.assign({}, columnsGroupingProps)),
        columnsSelectorComponent: react_1.default.createElement(ColumnsSelector_1.default, Object.assign({}, columnsSelectorProps)),
    };
    const containerHeaderContentProps = {
        disableFilters,
        loading,
        setGlobalFilter,
        globalFilter,
        styles,
        settingsBarComponent: react_1.default.createElement(SettingsBar_1.default, Object.assign({}, settingsBarProps)),
    };
    const containerProps = {
        actionGroup,
        gutters: false,
        title: tableTitle,
        subtitle: tableDescription,
        headerContent: disableFilters && disableSettings && disablePagination ? null : (react_1.default.createElement(ContainerHeaderContent_1.default, Object.assign({}, containerHeaderContentProps))),
    };
    const tableHeadProps = {
        headerGroups,
        styles,
    };
    const tableBodyProps = {
        reactTableBodyProps: getTableBodyProps(),
        page: page || [],
        wrapText,
        prepareRow,
        styles,
    };
    const tableFooterProps = {
        errorText,
        loading,
        styles,
        colSpan: columns.length,
        pageLength: page === null || page === void 0 ? void 0 : page.length,
    };
    return (react_1.default.createElement(Container_1.default, Object.assign({}, containerProps),
        react_1.default.createElement("div", { className: styles.tableWrapper },
            react_1.default.createElement(core_1.Table, Object.assign({}, getTableProps(), { size: "small", className: clsx_1.default({ [styles.loadingTableBlur]: loading }) }),
                react_1.default.createElement(TableHead_1.default, Object.assign({}, tableHeadProps)),
                react_1.default.createElement(TableBody_1.default, Object.assign({}, tableBodyProps)),
                react_1.default.createElement(TableFooter_1.default, Object.assign({}, tableFooterProps))))));
}
exports.default = Table;
//# sourceMappingURL=index.js.map